import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Home } from 'lucide-react';

interface PropertyDataStepProps {
  address: string;
  initialData?: {
    squareFootage?: number;
    bedrooms?: number;
    bathrooms?: number;
  };
  onSave: (propertyData: {
    squareFootage: number;
    bedrooms: number;
    bathrooms: number;
  }) => Promise<void>;
  loading?: boolean;
}

export const PropertyDataStep: React.FC<PropertyDataStepProps> = ({
  address,
  initialData,
  onSave,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    squareFootage: initialData?.squareFootage?.toString() || '',
    bedrooms: initialData?.bedrooms?.toString() || '',
    bathrooms: initialData?.bathrooms?.toString() || '',
    yearBuilt: ''
  });

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    await onSave({
      squareFootage: parseInt(formData.squareFootage) || 0,
      bedrooms: parseInt(formData.bedrooms) || 0,
      bathrooms: parseFloat(formData.bathrooms) || 0,
    });
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Property Information Needed</h1>
        <p className="text-gray-600">
          We need some details about your property at <strong>{address}</strong> to provide accurate renovation estimates.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Property Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            {initialData ? 
              "Please review and complete the property information below:" :
              "Our automated property lookup didn't find complete data. Please fill in the details:"
            }
          </p>
          
          <div className="grid gap-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Square Footage</label>
              <Input
                type="number"
                placeholder="2000"
                value={formData.squareFootage}
                onChange={(e) => handleChange('squareFootage', e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <label className="text-sm font-medium">Bedrooms</label>
              <Input
                type="number"
                placeholder="3"
                value={formData.bedrooms}
                onChange={(e) => handleChange('bedrooms', e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <label className="text-sm font-medium">Bathrooms</label>
              <Input
                type="number"
                step="0.5"
                placeholder="2.5"
                value={formData.bathrooms}
                onChange={(e) => handleChange('bathrooms', e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <label className="text-sm font-medium">Year Built (Optional)</label>
              <Input
                type="number"
                placeholder="1985"
                value={formData.yearBuilt}
                onChange={(e) => handleChange('yearBuilt', e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline">Skip for Now</Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? 'Saving...' : 'Save & Continue'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};